/* components/onboarding/StatsPreview.jsx -------------------------------
   A miniature of the statistics page's « Performance evolution » tiles
   (Statistic.jsx), with example figures, for the presentation of
   QRProofs. Drawn rather than a screenshot: it is sharp at any size and
   speaks the site language through the page's own texts (statistics.*).
-------------------------------------------------------------------- */
import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Example figures: rank up 2 places (lower is better), visits and
// returning customers up, rating up; `trend` is the sparkline, top = 0.
const TILES = [
  { label: 'rank', note: 'noteRank', value: 2, rank: true, delta: -2, trend: [8, 10, 14, 16, 22, 26, 30] },
  { label: 'visits', note: 'noteVerified', value: 148, delta: 23, pct: 18.4, trend: [30, 26, 28, 20, 18, 12, 6] },
  { label: 'repeaters', note: 'noteLoyalty', value: 37, delta: 6, pct: 19.4, trend: [28, 28, 24, 22, 16, 14, 10] },
  { label: 'avgRating', note: 'avgRatingWindow', value: 4.6, digits: 1, delta: 0.2, trend: [20, 22, 18, 16, 14, 12, 10] },
];

const sparkPoints = (trend) => trend.map((y, i) => `${(i * 220) / (trend.length - 1)},${y}`).join(' ');

export default function StatsPreview() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const fmt = (n, digits = 0) => new Intl.NumberFormat(lang, {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(n);

  return (
    <Box
      aria-hidden="true"
      sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: '#F3F7FC' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700 }}>{t('statistics.variations.title')}</Typography>
        <Typography variant="caption" color="text.secondary">{t('onboarding.example')}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
        {TILES.map((tile) => {
          const good = tile.rank ? tile.delta < 0 : tile.delta > 0;
          const delta = `${good ? '▲' : '▼'} ${fmt(Math.abs(tile.delta), tile.digits)}`
            + (tile.pct != null ? ` (+${fmt(tile.pct, 1)} %)` : '');
          return (
            <Paper key={tile.label} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography
                variant="overline"
                sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary', lineHeight: 1.4 }}
              >
                {t(`statistics.variations.${tile.label}`)}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: { xs: 32, sm: 40 }, fontWeight: 900, lineHeight: 1, letterSpacing: -0.5 }}>
                  {tile.rank ? `#${tile.value}` : fmt(tile.value, tile.digits)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {t(`statistics.variations.${tile.note}`)}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 700, color: good ? 'success.dark' : 'error.main' }}>
                {delta}
              </Typography>
              <svg width="100%" height="36" viewBox="0 0 220 36" preserveAspectRatio="none">
                <polyline points={sparkPoints(tile.trend)} fill="none" stroke="#1976D2" strokeWidth="2.5" />
              </svg>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
